import {
	ReactElement,
	useRef,
	useState,
	useLayoutEffect,
	useEffect,
	RefObject
} from "react";
import { AppImageDisplayInfo, RenderImageType } from "client/Api/AppIcon/common";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { sanitizeSvg } from "client/Misc/htmlUtils";
import { HttpClient } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./AppIcon.module.less";
import classNames from "classnames";

const INHERIT_COLOR = "inherit";

export type AppIconProps = {
	value: AppImageDisplayInfo;
	color?: string | typeof INHERIT_COLOR;
};

export const AppIcon = (props: AppIconProps): ReactElement => {
	const {
		value,
		color
	} = props;

	const {
		renderType,
		imageUrl
	} = value;

	const renderIcon = (): ReactElement => {
		switch (renderType) {
			case RenderImageType.Raster:
				return <RasterIcon url={imageUrl} />;
			case RenderImageType.Svg:
				return <SvgIcon url={imageUrl} />;
			case RenderImageType.ColorizableSvg:
				return <SvgIcon url={imageUrl} color={color} />;
			default:
				throw new UnreachableCaseError(renderType);
		}
	};

	return (
		<div className={styles.root}>
			{renderIcon()}
		</div>
	);
};
AppIcon.displayName = "AppIcon";

type RasterIconProps = {
	url: string;
};

const RasterIcon = (props: RasterIconProps): ReactElement => {
	const {
		url
	} = props;

	return (
		<img
			className={styles.image}
			src={url}
		/>
	);
};
RasterIcon.displayName = "RasterIcon";

type SvgIconProps = {
	url: string;
	color?: string | typeof INHERIT_COLOR;
};

const SvgIcon = (props: SvgIconProps): ReactElement => {
	const {
		url,
		color
	} = props;

	return color
		? <ColorizableSvgIconContent url={url} color={color} />
		: <NonColorizableSvgIconContent url={url} />;
};
SvgIcon.displayName = "SvgIcon";

type NonColorizableSvgIconContentProps = {
	url: string;
};

const NonColorizableSvgIconContent = (props: NonColorizableSvgIconContentProps): ReactElement => {
	const {
		url
	} = props;

	return (
		<img
			className={classNames(styles.image, styles.image_svg)}
			src={url}
		/>
	);
};
NonColorizableSvgIconContent.displayName = "NonColorizableSvgIconContent";

type ColorizableSvgIconContentProps = {
	url: string;
	color: string | typeof INHERIT_COLOR;
};

const ColorizableSvgIconContent = (props: ColorizableSvgIconContentProps): ReactElement => {
	const {
		url,
		color
	} = props;

	const [svgElement, setSvgElement] = useState<Element>();
	const [wasColorApplied, setWasColorApplied] = useState<boolean>(false);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	useLayoutEffect(
		() => {
			if (!svgElement) {
				return;
			}

			svgElement.setAttribute(
				"fill",
				color === INHERIT_COLOR
					? "currentColor"
					: color
			);

			setWasColorApplied(true);
		},
		[svgElement, color]
	);

	useEffect(
		() => {
			const load = async (url: string, signal: AbortSignal): Promise<void> => {
				const httpClient = new HttpClient();
				try {
					const data: string = await httpClient.getText(url, null, { cache: true, signal });

					const host: HTMLDivElement | null = ref.current;
					if (!host) {
						return;
					}

					while (host.firstChild) {
						host.removeChild(host.firstChild);
					}

					const wrapper = document.createElement("div");
					wrapper.innerHTML = sanitizeSvg(data);
					const svg: ChildNode | null = wrapper.firstChild;
					if (svg && svg.nodeName.toUpperCase() === "SVG") {
						host.appendChild(svg);
						setWasColorApplied(false);
						setSvgElement(svg as Element);
					} else {
						setSvgElement(undefined);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			};

			const cancellation = new AbortController();
			load(url, cancellation.signal);
			return () => cancellation.abort();
		},
		[url]
	);

	return (
		<div
			className={classNames(styles.image, styles.image_svg)}
			ref={ref}
			style={{ visibility: wasColorApplied ? undefined : "hidden" }}
		/>
	);
};
ColorizableSvgIconContent.displayName = "ColorizableSvgIconContent";
